/**
 * Spotlight.js — подсветка целевого элемента + тултип-коачмарк.
 *
 * Часть режиссуры демо (DemoTour). Находит элемент по CSS-селектору
 * (поллинг + ResizeObserver), рисует «дыру» в затемнении через box-shadow
 * и карточку-подсказку рядом. Затемнение не перехватывает клики
 * (pointer-events: none) — целевой элемент остаётся кликабельным.
 *
 * Все тексты приходят готовыми (i18n на стороне DemoTour).
 *
 * props:
 *   targetSelector — CSS-селектор подсвечиваемого элемента (опц.; без него — центр экрана)
 *   title, body    — тексты карточки
 *   ctaLabel, onCta            — основная кнопка (опц.)
 *   secondaryLabel, onSecondary— вторичная кнопка-ссылка (опц.)
 *   placement      — 'auto' | 'top' | 'bottom' (по умолчанию 'auto')
 *   padding        — отступ дыры вокруг элемента (px, по умолчанию 8)
 */
import React, { useEffect, useState, useRef } from 'react';

const PILL = 'rgba(99,102,241,1)'; // var(--accent) демо

export default function Spotlight({
  targetSelector,
  title,
  body,
  ctaLabel,
  onCta,
  secondaryLabel,
  onSecondary,
  placement = 'auto',
  padding = 8,
  noDim = false,
  zIndex = 10006,
  // Якорь карточки, когда нет таргета: 'bottom' (по центру снизу, по умолчанию)
  // или 'top' (вверху справа — у колокольчика уведомлений).
  anchor = 'bottom',
  // Блокировать клики вне выреза (маски ловят pointer-events). Используется когда
  // нужно оставить активной ТОЛЬКО подсвеченную цель (напр. кнопка «Назначить работу»).
  blockOutside = false,
}) {
  const [rect, setRect] = useState(null); // bounding rect целевого элемента
  const rafRef = useRef(null);

  // Поиск элемента + отслеживание его позиции (скролл, ресайз, перерендеры).
  // Если цель пропала (модал закрыли, ушли со страницы) — сбрасываем подсветку,
  // чтобы не оставлять висящий артефакт.
  useEffect(() => {
    setRect(null);
    if (!targetSelector) return;
    let cancelled = false;
    let scrolled = false;
    let misses = 0;

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(targetSelector);
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 || r.height > 0) {
          misses = 0;
          if (!scrolled) { scrolled = true; el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        }
      } else {
        misses += 1;
        if (misses > 20) setRect(null); // ~0.3s без цели → гасим подсветку
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => { cancelled = true; cancelAnimationFrame(rafRef.current); };
  }, [targetSelector]);

  const hasTarget = !!rect;

  // Геометрия дыры
  const hole = hasTarget
    ? {
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      }
    : null;

  // Позиция карточки: под целью, либо над, если снизу мало места
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const cardBelow = hasTarget
    ? (placement === 'top' ? false : placement === 'bottom' ? true : (hole.top + hole.height + 180 < vh))
    : true;

  const cardStyle = hasTarget
    ? {
        position: 'fixed',
        left: Math.max(16, Math.min(hole.left, (typeof window !== 'undefined' ? window.innerWidth : 400) - 360 - 16)),
        ...(cardBelow
          ? { top: hole.top + hole.height + 14 }
          : { top: Math.max(16, hole.top - 14), transform: 'translateY(-100%)' }),
      }
    : anchor === 'top'
      ? {
          // Вверху справа, под колокольчиком уведомлений (учёт «чёлки» iOS).
          position: 'fixed',
          top: 'calc(env(safe-area-inset-top, 0px) + 64px)',
          right: 16,
        }
      : {
          position: 'fixed',
          left: '50%',
          bottom: 24,
          transform: 'translateX(-50%)',
        };

  return (
    <div
      data-ph-capture-attribute-component="demo_spotlight"
      style={{ position: 'fixed', inset: 0, zIndex, pointerEvents: 'none' }}
    >
      {/* Затемнение с «дырой» вокруг цели. 4 маски ловят клики мимо —
          стрэй-тапы не проваливаются вниз и не сбивают гайд. Дыра над целью открыта. */}
      {hasTarget ? (() => {
        const W = typeof window !== 'undefined' ? window.innerWidth : 400;
        const H = typeof window !== 'undefined' ? window.innerHeight : 800;
        const DIM = 'rgba(0,0,0,0.55)';
        const ht = Math.max(0, hole.top), hl = Math.max(0, hole.left);
        const masks = [
          { top: 0, left: 0, width: W, height: ht },
          { top: hole.top + hole.height, left: 0, width: W, height: Math.max(0, H - (hole.top + hole.height)) },
          { top: ht, left: 0, width: hl, height: hole.height },
          { top: ht, left: hole.left + hole.width, width: Math.max(0, W - (hole.left + hole.width)), height: hole.height },
        ];
        return (
          <>
            {/* Маски чисто визуальные (pointer-events:none) — иначе они съедают скролл страницы.
                Стрэй-тапы обезврежены иначе: FAB скрыт на время гайда, показательная задача авто-развёрнута. */}
            {masks.map((m, i) => (
              <div key={i} style={{ position: 'fixed', ...m, background: DIM, pointerEvents: blockOutside ? 'auto' : 'none' }} />
            ))}
            <div style={{
              position: 'fixed', top: hole.top, left: hole.left, width: hole.width, height: hole.height,
              borderRadius: 12, outline: `2px solid ${PILL}`, pointerEvents: 'none',
            }} />
          </>
        );
      })() : (!noDim && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', pointerEvents: 'none' }} />
      ))}

      {/* Карточка-подсказка */}
      <div
        style={{
          ...cardStyle,
          width: 'min(360px, calc(100vw - 32px))',
          background: 'var(--bg2)',
          borderRadius: 14,
          padding: '18px 20px',
          boxShadow: '0 12px 48px rgba(0,0,0,0.3)',
          pointerEvents: 'auto',
        }}
      >
        {title && (
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
            {title}
          </div>
        )}
        {body && (
          <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.5, marginBottom: (ctaLabel || secondaryLabel) ? 16 : 0 }}>
            {body}
          </div>
        )}
        {(ctaLabel || secondaryLabel) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ctaLabel && (
              <button
                onClick={onCta}
                data-ph-capture-attribute-action="spotlight_cta"
                style={{
                  background: PILL, color: '#fff', border: 'none',
                  padding: '11px 16px', borderRadius: 9, fontSize: 14, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {ctaLabel}
              </button>
            )}
            {secondaryLabel && (
              <button
                onClick={onSecondary}
                data-ph-capture-attribute-action="spotlight_secondary"
                style={{
                  background: 'none', border: 'none', color: 'var(--text3, #888)',
                  fontSize: 13, cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                {secondaryLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
